/**
 * plugins/cover.ts — Capa de documento (.cover)
 *
 * Ferramentas:
 *   - Editar título principal (.cover-title)
 *   - Editar subtítulo (.cover-subtitle)
 *   - Editar badge de categoria (.cover-badge ou span.badge dentro do cover)
 *   - Editar URL da imagem de fundo (.cover-bg / style background-image)
 *   - Adicionar campo meta (label + valor)
 *   - Adicionar badge inline ao título
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside } from '../types';

export class CoverPlugin implements VtbPlugin {
    readonly panelId = 'vtb-cover';
    readonly selectorHints = ['div.cover', '.cover', '[data-vtb-id="cover"]'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        const el = closestInside(target, visualEditor, e =>
            e.classList.contains('cover') || e.dataset.vtbId === 'cover'
        );
        if (!el) return null;
        return { type: this.panelId, label: 'Capa', el };
    }

    activate(ctx: SmartCtx, registry: IVtbRegistry): void {
        const el = ctx.el;

        // Título
        const titleEl = el.querySelector<HTMLElement>('.cover-title, [data-vtb-id="cover-title"]');
        const titleIn = registry.q<HTMLInputElement>('vtb-cover-title');
        if (titleIn) titleIn.value = titleEl?.textContent?.trim() ?? '';

        // Subtítulo
        const subtitleEl = el.querySelector<HTMLElement>('.cover-subtitle, [data-vtb-id="cover-subtitle"]');
        const subtitleIn = registry.q<HTMLInputElement>('vtb-cover-subtitle');
        if (subtitleIn) subtitleIn.value = subtitleEl?.textContent?.trim() ?? '';

        // Badge de categoria
        const badgeEl = el.querySelector<HTMLElement>('.cover-badge, [data-vtb-id="cover-badge"]')
                     ?? el.querySelector<HTMLElement>(':scope > span.badge, :scope > .cover-header > span.badge');
        const badgeIn = registry.q<HTMLInputElement>('vtb-cover-badge');
        if (badgeIn) badgeIn.value = badgeEl?.textContent?.trim() ?? '';

        // Imagem de fundo
        const bgEl = el.querySelector<HTMLElement>('.cover-bg, [data-vtb-id="cover-bg"]') ?? el;
        const bgUrl = this._extractBgUrl(bgEl.style.backgroundImage || bgEl.style.background);
        const bgIn = registry.q<HTMLInputElement>('vtb-cover-bg-url');
        if (bgIn) bgIn.value = bgUrl;

        // Reset do form de meta
        const addForm = registry.q<HTMLElement>('vtb-cover-meta-add-form');
        if (addForm) addForm.style.display = 'none';
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-cover-apply')?.addEventListener('click', () => this._apply(registry));

        // Live preview do título
        registry.q('vtb-cover-title')?.addEventListener('input', () => this._applyTitle(registry));
        registry.q('vtb-cover-subtitle')?.addEventListener('input', () => this._applySubtitle(registry));
        registry.q('vtb-cover-badge')?.addEventListener('input', () => this._applyBadge(registry));
        registry.q('vtb-cover-bg-url')?.addEventListener('change', () => this._applyBg(registry));

        // Meta
        registry.q('vtb-cover-meta-open')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-cover-meta-add-form');
            if (f) f.style.display = f.style.display === 'none' ? 'flex' : 'none';
        });
        registry.q('vtb-cover-meta-add')?.addEventListener('click', () => this._addMeta(registry));
        registry.q('vtb-cover-meta-cancel')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-cover-meta-add-form');
            if (f) f.style.display = 'none';
        });

        // Adicionar badge ao título
        registry.q('vtb-cover-badge-add')?.addEventListener('click', () => this._addBadgeToTitle(registry));
    }

    // ── Aplica todos os campos de uma vez ─────────────────────────────────────

    private _apply(registry: IVtbRegistry): void {
        this._applyTitle(registry);
        this._applySubtitle(registry);
        this._applyBadge(registry);
        this._applyBg(registry);
    }

    private _applyTitle(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const text = registry.q<HTMLInputElement>('vtb-cover-title')?.value ?? '';
        let titleEl = ctx.el.querySelector<HTMLElement>('.cover-title, [data-vtb-id="cover-title"]');
        if (!titleEl && text) {
            titleEl = document.createElement('h1');
            titleEl.className = 'cover-title';
            titleEl.dataset.vtbId = 'cover-title';
            ctx.el.prepend(titleEl);
        }
        if (titleEl) {
            // Preserva badges dentro do título
            const badge = titleEl.querySelector('.badge');
            if (badge) {
                const textNode = Array.from(titleEl.childNodes).find(n => n.nodeType === Node.TEXT_NODE);
                if (textNode) textNode.textContent = text + ' ';
                else titleEl.insertBefore(document.createTextNode(text + ' '), badge);
            } else {
                titleEl.textContent = text;
            }
        }
        registry.sync();
    }

    private _applySubtitle(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const text = registry.q<HTMLInputElement>('vtb-cover-subtitle')?.value ?? '';
        let el = ctx.el.querySelector<HTMLElement>('.cover-subtitle, [data-vtb-id="cover-subtitle"]');
        if (!el && text) {
            el = document.createElement('p');
            el.className = 'cover-subtitle';
            el.dataset.vtbId = 'cover-subtitle';
            const titleEl = ctx.el.querySelector('.cover-title');
            if (titleEl) titleEl.after(el);
            else ctx.el.appendChild(el);
        }
        if (el) el.textContent = text;
        registry.sync();
    }

    private _applyBadge(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const text = registry.q<HTMLInputElement>('vtb-cover-badge')?.value?.trim() ?? '';

        let badgeEl = ctx.el.querySelector<HTMLElement>('.cover-badge, [data-vtb-id="cover-badge"]')
                   ?? ctx.el.querySelector<HTMLElement>(':scope > span.badge, :scope > .cover-header > span.badge');

        if (!text) {
            badgeEl?.remove();
            registry.sync();
            return;
        }

        if (!badgeEl) {
            badgeEl = document.createElement('span');
            badgeEl.className = 'badge cover-badge';
            badgeEl.dataset.vtbId = 'cover-badge';
            ctx.el.prepend(badgeEl);
        }
        badgeEl.textContent = text;
        registry.sync();
    }

    private _applyBg(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const url = registry.q<HTMLInputElement>('vtb-cover-bg-url')?.value?.trim() ?? '';
        const bgEl = ctx.el.querySelector<HTMLElement>('.cover-bg, [data-vtb-id="cover-bg"]') ?? ctx.el;
        if (url) {
            bgEl.style.backgroundImage = `url('${url}')`;
        } else {
            bgEl.style.removeProperty('background-image');
            bgEl.style.removeProperty('background');
        }
        registry.sync();
    }

    // ── Meta fields ───────────────────────────────────────────────────────────

    private _addMeta(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        const labelText = registry.q<HTMLInputElement>('vtb-cover-meta-label')?.value?.trim() || 'Campo';
        const valueText = registry.q<HTMLInputElement>('vtb-cover-meta-value')?.value?.trim() || 'Valor';

        let meta = ctx.el.querySelector<HTMLElement>('.meta, [data-vtb-id="meta"]');
        if (!meta) {
            meta = document.createElement('div');
            meta.className = 'meta';
            meta.dataset.vtbId = 'meta';
            ctx.el.appendChild(meta);
        }

        const label = document.createElement('span');
        label.className = 'label';
        label.dataset.vtbId = 'meta-label';
        label.textContent = labelText;

        const value = document.createElement('span');
        value.dataset.vtbId = 'meta-value';
        value.textContent = valueText;

        meta.appendChild(label);
        meta.appendChild(value);

        // Reset form
        const f = registry.q<HTMLElement>('vtb-cover-meta-add-form');
        if (f) f.style.display = 'none';
        const li = registry.q<HTMLInputElement>('vtb-cover-meta-label');
        const vi = registry.q<HTMLInputElement>('vtb-cover-meta-value');
        if (li) li.value = '';
        if (vi) vi.value = '';

        registry.sync();
        registry.selectEl(value);
    }

    // ── Badge inline no título ────────────────────────────────────────────────

    private _addBadgeToTitle(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;

        const titleEl = ctx.el.querySelector<HTMLElement>('.cover-title, [data-vtb-id="cover-title"]');
        if (!titleEl) return;

        const badge = document.createElement('span');
        badge.className = 'badge badge-new';
        badge.textContent = 'Novo';
        titleEl.appendChild(badge);

        registry.sync();
        registry.selectEl(badge);
    }

    // ── Utilitário ────────────────────────────────────────────────────────────

    private _extractBgUrl(bgValue: string): string {
        if (!bgValue) return '';
        const m = bgValue.match(/url\(['"]?([^'"()]+)['"]?\)/);
        return m ? m[1] : '';
    }
}
